// ─── Event Types & Configuration ─────────────────────────

export const EVENT_TYPE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
    retreat:  { label: 'types.retreat',  icon: 'leaf-outline',      color: '#2D8A4E' },
    workshop: { label: 'types.workshop', icon: 'construct-outline',  color: '#6B5CE7' },
    ceremony: { label: 'types.ceremony', icon: 'flame-outline',      color: '#E98245' },
    class:    { label: 'types.class',    icon: 'school-outline',     color: '#2196F3' },
    festival: { label: 'types.festival', icon: 'sparkles-outline',   color: '#E91E8C' },
    meetup:   { label: 'types.meetup',   icon: 'people-outline',     color: '#00897B' },
    practice: { label: 'types.practice', icon: 'body-outline',       color: '#FF6F00' },
};

// ─── Format Filter ───────────────────────────────────────
// Two top-level groups: Live (real-time) and Events (larger/longer)

export interface FilterCategory {
    key: string;
    label: string;
    icon: string;
}

export interface FilterGroup {
    key: string;
    label: string;
    icon: string;
    items: FilterCategory[];
}

/**
 * FORMAT FILTER — "What kind of session?"
 * label values are i18n keys resolved via t(label, { ns: 'events' })
 */
export const FORMAT_GROUPS: FilterGroup[] = [
    {
        key: 'live',
        label: 'formatGroup.live',
        icon: 'radio-outline',
        items: [
            { key: 'live_meditation',   label: 'format.live_meditation',   icon: 'leaf-outline' },
            { key: 'guided_practice',   label: 'format.guided_practice',   icon: 'navigate-outline' },
            { key: 'breathwork',        label: 'format.breathwork',        icon: 'pulse-outline' },
            { key: 'movement',          label: 'format.movement',          icon: 'body-outline' },
            { key: 'somatic',           label: 'format.somatic',           icon: 'hand-left-outline' },
            { key: 'sound_bath',        label: 'format.sound_bath',        icon: 'musical-notes-outline' },
            { key: 'mantra',            label: 'format.mantra',            icon: 'mic-outline' },
            { key: 'mindfulness',       label: 'format.mindfulness',       icon: 'eye-outline' },
            { key: 'coaching_group',    label: 'format.coaching_group',    icon: 'people-outline' },
            { key: 'talk',              label: 'format.talk',              icon: 'school-outline' },
            { key: 'qa',                label: 'format.qa',                icon: 'chatbubbles-outline' },
            { key: 'community_circle',  label: 'format.community_circle',  icon: 'heart-circle-outline' },
            { key: 'deep_dive',         label: 'format.deep_dive',         icon: 'color-wand-outline' },
            { key: 'morning_ritual',    label: 'format.morning_ritual',    icon: 'sunny-outline' },
            { key: 'evening_winddown',  label: 'format.evening_winddown',  icon: 'moon-outline' },
        ],
    },
    {
        key: 'events',
        label: 'formatGroup.events',
        icon: 'calendar-outline',
        items: [
            { key: 'workshop',             label: 'format.workshop',             icon: 'construct-outline' },
            { key: 'masterclass',          label: 'format.masterclass',          icon: 'trophy-outline' },
            { key: 'intensive',            label: 'format.intensive',            icon: 'flash-outline' },
            { key: 'live_series',          label: 'format.live_series',          icon: 'layers-outline' },
            { key: 'course_cohort',        label: 'format.course_cohort',        icon: 'book-outline' },
            { key: 'retreat_online',       label: 'format.retreat_online',       icon: 'desktop-outline' },
            { key: 'retreat_inperson',     label: 'format.retreat_inperson',     icon: 'leaf-outline' },
            { key: 'immersion',            label: 'format.immersion',            icon: 'bonfire-outline' },
            { key: 'challenge',            label: 'format.challenge',            icon: 'ribbon-outline' },
            { key: 'ceremony',             label: 'format.ceremony',             icon: 'flame-outline' },
            { key: 'sound_journey',        label: 'format.sound_journey',        icon: 'musical-note-outline' },
            { key: 'experience_night',     label: 'format.experience_night',     icon: 'sparkles-outline' },
            { key: 'meetup',               label: 'format.meetup',               icon: 'location-outline' },
            { key: 'private_1on1',         label: 'format.private_1on1',         icon: 'person-outline' },
            { key: 'festival',             label: 'format.festival',             icon: 'globe-outline' },
            { key: 'facilitator_training', label: 'format.facilitator_training', icon: 'school-outline' },
            { key: 'certification',        label: 'format.certification',        icon: 'shield-checkmark-outline' },
        ],
    },
];

/**
 * PATH/STYLE FILTER — "What approach resonates?"
 * label values are i18n keys resolved via t(label, { ns: 'events' })
 */
export const PATH_GROUPS: FilterGroup[] = [
    {
        key: 'science',
        label: 'pathGroup.science',
        icon: 'flask-outline',
        items: [
            { key: 'mbsr',            label: 'path.mbsr',            icon: 'analytics-outline' },
            { key: 'stress_reset',    label: 'path.stress_reset',    icon: 'battery-charging-outline' },
            { key: 'anxiety',         label: 'path.anxiety',         icon: 'shield-outline' },
            { key: 'sleep',           label: 'path.sleep',           icon: 'moon-outline' },
            { key: 'focus',           label: 'path.focus',           icon: 'bulb-outline' },
            { key: 'emotional_reg',   label: 'path.emotional_reg',   icon: 'heart-outline' },
            { key: 'self_compassion', label: 'path.self_compassion', icon: 'flower-outline' },
            { key: 'cbt',             label: 'path.cbt',             icon: 'git-branch-outline' },
            { key: 'somatic_psych',   label: 'path.somatic_psych',   icon: 'body-outline' },
            { key: 'nervous_system',  label: 'path.nervous_system',  icon: 'pulse-outline' },
            { key: 'trauma_sensitive',label: 'path.trauma_sensitive', icon: 'hand-left-outline' },
            { key: 'mental_fitness',  label: 'path.mental_fitness',  icon: 'fitness-outline' },
        ],
    },
    {
        key: 'body',
        label: 'pathGroup.body',
        icon: 'body-outline',
        items: [
            { key: 'somatic_release', label: 'path.somatic_release', icon: 'water-outline' },
            { key: 'breath_modern',   label: 'path.breath_modern',   icon: 'pulse-outline' },
            { key: 'vagus_toning',    label: 'path.vagus_toning',    icon: 'mic-outline' },
            { key: 'mobility',        label: 'path.mobility',        icon: 'resize-outline' },
            { key: 'yin_relax',       label: 'path.yin_relax',       icon: 'leaf-outline' },
            { key: 'movement_med',    label: 'path.movement_med',    icon: 'walk-outline' },
            { key: 'qigong',          label: 'path.qigong',          icon: 'sync-outline' },
            { key: 'posture',         label: 'path.posture',         icon: 'body-outline' },
            { key: 'grounding',       label: 'path.grounding',       icon: 'earth-outline' },
            { key: 'bioenergetic',    label: 'path.bioenergetic',    icon: 'flash-outline' },
        ],
    },
    {
        key: 'spiritual',
        label: 'pathGroup.spiritual',
        icon: 'sparkles-outline',
        items: [
            { key: 'heart_practice',  label: 'path.heart_practice',  icon: 'heart-outline' },
            { key: 'awareness',       label: 'path.awareness',       icon: 'eye-outline' },
            { key: 'intuition',       label: 'path.intuition',       icon: 'compass-outline' },
            { key: 'energy',          label: 'path.energy',          icon: 'flash-outline' },
            { key: 'chakras',         label: 'path.chakras',         icon: 'ellipse-outline' },
            { key: 'sound_vibration', label: 'path.sound_vibration', icon: 'musical-notes-outline' },
            { key: 'ritual',          label: 'path.ritual',          icon: 'flame-outline' },
            { key: 'nature',          label: 'path.nature',          icon: 'earth-outline' },
            { key: 'manifestation',   label: 'path.manifestation',   icon: 'star-outline' },
            { key: 'shadow_work',     label: 'path.shadow_work',     icon: 'moon-outline' },
        ],
    },
];

// ─── Legacy compatibility (Traditions → now "All" in new model) ──
export const TRADITIONS = [
    { key: 'all',         label: 'traditions.all',         icon: 'grid-outline' },
    { key: 'buddhism',    label: 'traditions.buddhism',    icon: 'flower-outline' },
    { key: 'hinduism',    label: 'traditions.hinduism',    icon: 'flame-outline' },
    { key: 'sufism',      label: 'traditions.sufism',      icon: 'heart-outline' },
    { key: 'shaivism',    label: 'traditions.shaivism',    icon: 'eye-outline' },
    { key: 'yoga',        label: 'traditions.yoga',        icon: 'body-outline' },
    { key: 'taoism',      label: 'traditions.taoism',      icon: 'water-outline' },
    { key: 'mindfulness', label: 'traditions.mindfulness', icon: 'leaf-outline' },
    { key: 'tantra',      label: 'traditions.tantra',      icon: 'sparkles-outline' },
] as const;

export type TraditionKey = typeof TRADITIONS[number]['key'];
export type FormatKey = string;
export type PathKey = string;
